import {
    KeyboardEvent,
    ClipboardEvent,
    useState,
    useEffect, useCallback
} from 'react';
import { IServerValidation } from '../../../../../domain/validation/interfaces';
import ValidationErrors from '../../../../../domain/validation/server-errors/validation-errors';
import {
    createControl,
    updateControl,
    validateForm,
    removeInvisibleCharsOnPaste,
    validateControl
} from '../../../../../domain/validation/validation';
import { getSimpleCaptchaValidationError } from '../../../../../domain/captcha/simple-captcha';
import { getIsChina } from '../../../../../domain/hidden-data-provider';
import { getRecaptchaToken, renderRecaptcha2, resetRecaptcha2, getRecaptcha2Error } from '../../../../../domain/captcha/recaptcha-provider';
import Recaptcha3Text from '../../../../../domain/captcha/recaptcha3-terms';
import { IFormControl } from '../../../../../ui/forms/interfaces';
import FormTextField from '../../../../../ui/forms/fields/text-field';
import FormCheckboxField from '../../../../../ui/forms/fields/checkbox-field';
import ChinaCaptchaField from '../../../../../ui/forms/fields/captcha-china-field';
import FormPreloader from '../../../../../ui/forms/form-preloader';
import { ILoginReferrer, login } from '../../../../membership/dal/data-provider';
import { billingAddressActions, rootActions, useAppDispatch, useAppSelector } from '../../data/store';
import { CartSteps } from '../../enums/cart-steps';
import { getLocalData } from '../../domain/shopping-cart-address-provider';
import { rvpSetNullToStorage } from '../../../../recently-viewed-products/data/data-service';
import ExternalLogin from '../../../../membership/login/external-login/external-login';
import {
    GOOGLE_ERROR,
    GOOGLE_SIGN_IN_INITIALIZED,
    GOOGLE_SIGNED_IN
} from '../../../../../domain/google/google-login-provider';
import { sendGAWavesUserGuidEvent } from '../../../../../domain/marketing/gtm-provider';

/**
 * Shopping Cart Login
 */
const CartLogin = () => {

    const dispatch = useAppDispatch();
    const isChina = getIsChina();

    // validation ---------------------------------------
    const userNameControl = createControl('userName', '', {
        required: true
    });
    const passwordControl = createControl('password', '', {
        required: true
    });
    const simpleCaptchaControl = createControl('simpleCaptcha', '', {
        required: true,
        requiredMessage: getSimpleCaptchaValidationError(),
        length: 5,
    });
    const referCreditControl = useAppSelector(store => store.root.referCreditControl);

    const [isLoading, setIsLoading] = useState(false);
    const [response, setResponse] = useState<IServerValidation|null>();
    const [userName, setUserName] = useState<IFormControl>(userNameControl);
    const [password, setPassword] = useState<IFormControl>(passwordControl);
    const [isPersistent, setIsPersistent] = useState(true);

    // simple captcha - used for chine
    const [simpleCaptchaCode, setSimpleCaptchaCode] = useState<IFormControl>(simpleCaptchaControl);
    const [simpleCaptchaAttempts, setSimpleCaptchaAttempts] = useState(0);

    // google recaptcha version 2 & 3
    const [recaptchaVersion, setRecaptchaVersion] = useState(3);
    const [recaptcha2WidgetID, setRecaptcha2WidgetID] = useState(0);

    // captcha: common
    const [captchaError, setCaptchaError] = useState('');

    // this country / state data is used for tax calculations
    const updateCount = useAppSelector(store => store.billingAddress.updateCount);

    useEffect(() => {
        document.body.dispatchEvent(new CustomEvent(GOOGLE_SIGN_IN_INITIALIZED));
    }, []);

    /**
     * if recaptcha 3 returns low score (the limit is defined in kentico settings),
     * then recaptcha 2 fallback should be rendered inside <div id="recaptcha"></div>
     */
    const renderCaptcha2Fallback = () => {
        if(!window.grecaptcha) return;

        window.grecaptcha.ready(async () => {
            const widgetID = await renderRecaptcha2('recaptcha');
            setRecaptcha2WidgetID(widgetID);
        });
    };

    const onLoginSuccess = useCallback( (response: { data: IServerValidation, statusCode: number }) => {

        // update cart data
        dispatch(
            rootActions.setCartData(response.data.relatedData)
        );

        // update the address controls from the login response
        dispatch(
            billingAddressActions.main({
                updateCount: updateCount + 1,
            })
        );

        // clear recently viewed products storage
        rvpSetNullToStorage();

        dispatch(
            rootActions.setControl({
                controlName: 'referCreditControl',
                control: updateControl(
                    referCreditControl,
                    response.data.relatedData?.priceData?.referCredit?.toString() || '',
                    false,
                )
            })
        );

        sendGAWavesUserGuidEvent(response.data.relatedData.gA_GUID);

        // navigate to the third step
        dispatch(
            rootActions.main({
                cartStep: CartSteps.Third,
            })
        );
    }, [ dispatch, referCreditControl, updateCount ]);

    const onLoginError = useCallback((response: { data: IServerValidation, statusCode: number } | null) => {
        if(isChina){

            // re-render simple captcha
            setSimpleCaptchaAttempts(simpleCaptchaAttempts + 1);
        }
        else{
            if(response?.data.relatedData === 'recaptcha-error') {
                setRecaptchaVersion(2);
                renderCaptcha2Fallback();
            }

            if(recaptchaVersion === 2){
                resetRecaptcha2();
            }
        }

        // render errors
        setResponse(response?.data);
    }, [ isChina, recaptchaVersion, simpleCaptchaAttempts ]);

    /**
     * perform user login on server
     */
    const performLogin = async () => {

        // client side validation
        const isValid =
            isChina ?
                validateForm([
                    [userName, setUserName],
                    [password, setPassword],
                    [simpleCaptchaCode, setSimpleCaptchaCode],
                ]) :
                validateForm([
                    [userName, setUserName],
                    [password, setPassword],
                ]);

        if(!isValid) return;

        setIsLoading(true);

        let response = null;

        try {
            const captchaToken = !isChina ? await getRecaptchaToken(recaptchaVersion) : simpleCaptchaCode.value;

            if(!captchaToken){
                setCaptchaError(isChina ? getSimpleCaptchaValidationError() : getRecaptcha2Error());
                setIsLoading(false);
                return;
            }

            response = await login(
                ILoginReferrer.CartPage,
                userName.value,
                password.value,
                isPersistent,
                captchaToken,
                recaptchaVersion,

                getLocalData()
            );
        }
        catch(ex){
            console.log(ex);
        }

        if(
            response &&
            response.statusCode === 200 &&
            response.data &&
            response.data.isValid){

            // all is well ---------------
            onLoginSuccess(response);
            return;
        }

        // handle errors ---------
        setIsLoading(false);
        onLoginError(response);
    };

    /**
     * and enter / return keys
     */
    const handleEnter = async (evt: KeyboardEvent<HTMLInputElement>) => {
        if(evt.key === 'Enter') {
            await performLogin();
        }
    };

    /**
     * empty errors on click
     */
    const handleFieldClick = () =>{
        setResponse(null);
        setCaptchaError('');
    };

    const googleSignedIn = useCallback(async (evt: CustomEvent) => {
        if(!evt || !evt.detail) return;
        await onLoginSuccess(evt.detail);
    }, [ onLoginSuccess ]);

    const googleError = useCallback((evt: CustomEvent) => {
        if(!evt || !evt.detail) return;
        onLoginError(evt.detail);
    }, [ onLoginError ]);

    useEffect(() => {
        // @ts-ignore
        document.body.addEventListener(GOOGLE_ERROR, googleError);
        // @ts-ignore
        document.body.addEventListener(GOOGLE_SIGNED_IN, googleSignedIn);

        return () => {
            // @ts-ignore
            document.body.removeEventListener(GOOGLE_ERROR, googleError);
            // @ts-ignore
            document.body.removeEventListener(GOOGLE_SIGNED_IN, googleSignedIn);
        };
    }, [googleError, googleSignedIn]);

    const externalLoginCallback = (response: { data: IServerValidation, statusCode: number } | null) => {
        if( response &&
            response.statusCode === 200 &&
            response.data &&
            response.data.isValid) {
            onLoginSuccess(response);
        }
        else{
            // render errors
            onLoginError(response);
        }
    };

    return (
        <div
            style={{
                width: '350px',
                maxWidth: '100%'
            }}>

            <ExternalLogin
                facebookText="Sign in with Facebook"
                googleTextParam="signin_with"
                dividerText="Or, sign in with your username"
                isShoppingCart={ true }
                callback={ externalLoginCallback }
                localData={ getLocalData() }
            />

            <FormTextField
                id={ 'username-tb' }
                label={ 'Username' }
                control={ userName }
                maxLength={ 100 }
                qa={ 'login-username' }
                onChange={ evt => {
                    const $target = evt.target as HTMLInputElement;
                    setUserName(updateControl(userName, $target.value, !userName.isValid));
                    setResponse(null); // empty server error
                    setCaptchaError('');
                }}
                onBlur={ () => {
                    setUserName(validateControl(userName));
                }}
                onClick={ handleFieldClick }
                onKeyUp={ handleEnter }
                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                    setUserName(updateControl(userName, removeInvisibleCharsOnPaste(evt)));
                }}
            />

            <FormTextField
                id={ 'password-tb' }
                label={ 'Password' }
                control={ password }
                maxLength={ 110 }
                qa={ 'login-password' }
                type={ 'password' }
                onChange={ evt => {
                    const $target = evt.target as HTMLInputElement;
                    setPassword(updateControl(password, $target.value, !password.isValid));
                    setResponse(null); // empty server error
                    setCaptchaError('');
                }}
                onBlur={ () => {
                    setPassword(validateControl(password));
                }}
                onClick={ handleFieldClick }
                onKeyUp={ handleEnter }
                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                    setPassword(updateControl(password, removeInvisibleCharsOnPaste(evt)));
                }}
            />

            <FormCheckboxField
                id={ 'login-remember-me-cb' }
                label={ 'Remember me' }
                isChecked={ isPersistent }
                onChange={ evt => {
                    setIsPersistent(val => !val);
                }}
                onClick={ handleFieldClick }
                additionalContent={
                    <div className="flex items-center">
                        <a className="text-sm no-underline" href="/forgot-password" title="" data-qa="login-forgot-password">Forgot Password?</a>
                    </div>
                }
            />

            {
                /* ------------- google recaptcha version 2 or 3 ------------- */
                !isChina &&
                <div id="recaptcha" className={`mb-4 flex justify-center recaptcha recaptcha-version-${ recaptchaVersion }`}/>
            }

            {
                /* ------------- china simple captcha ------------- */
                isChina &&
                <ChinaCaptchaField
                    control={ simpleCaptchaCode }
                    setSimpleCaptchaCode={ setSimpleCaptchaCode }
                    simpleCaptchaAttempts={ simpleCaptchaAttempts }
                />
            }

            <div className="mb-2">

                { isLoading && <FormPreloader /> }

                {
                    !isLoading &&
                    <button
                        className="w-btn"
                        type="button"
                        data-qa="login-button"
                        onClick={async evt => {
                            await performLogin();
                        }}
                    >Log in</button>
                }
            </div>

            {
                response && !response.isValid && (
                    <div className="text-sm text-red my-4 text-center">
                        <ValidationErrors errors={ response.errors } />
                    </div>
                )
            }

            {
                captchaError &&
                <div className="text-sm text-red my-4 text-center" data-id={ recaptcha2WidgetID }>{ captchaError }</div>
            }

            {
                !isChina && recaptchaVersion === 3 &&
                <div className="mt-8 text-center">
                    <Recaptcha3Text />
                </div>
            }

        </div>
    )
};

export default  CartLogin;